import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import i18n from "i18n";
import UserNotification from "util/UserNotification";
import { defaultCompare } from "logic/DefaultCompare";
import { defaultOnError } from "util/conditional/onError";
import {
  bulkSigmaRepoDelete,
  fetchStreams,
  fetchNotifications,
  listGitRepos,
  addGitRepo,
  updateGitRepo,
  deleteSigmaRepo,
  listRepoContent,
  addDefaultRepo,
  importAllRulesFromRepo,
  refreshRepoRules,
  validateSigmaRuleZip,
  validateSigmaRuleYaml,
  bulkImportFromRepo,
  bulkUploadEventDefs
} from "./api/sigmaAPI";
const sigmaRulesKey = "get-sigma-rules";
export function useGetStreams(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["streams-index"],
    queryFn: () => defaultOnError(fetchStreams(), i18n.t("notifications.fetch_streams_failed", { ns: "enterprise_security_sigma" })),
    retry: 1,
    enabled: execute
  });
  return {
    loadingStreams: isLoading && !!execute,
    streams: (data == null ? void 0 : data.streams.sort((a, b) => {
      if (a.title > b.title) return 1;
      if (a.title < b.title) return -1;
      return 0;
    })) || [],
    streamCategories: (data == null ? void 0 : data.streams.reduce((acc, stream) => {
      var _a;
      (_a = stream.categories) == null ? void 0 : _a.forEach((category) => {
        if (!acc.includes(category)) acc.push(category);
      });
      return acc;
    }, []).sort((a, b) => defaultCompare(a, b))) || []
  };
}
export function useGetNotifications(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["notifications-index"],
    queryFn: () => defaultOnError(
      fetchNotifications(),
      i18n.t("notifications.fetch_notifications_failed", { ns: "enterprise_security_sigma" })
    ),
    retry: 1,
    enabled: execute
  });
  return {
    loadingNotifications: isLoading && !!execute,
    notifications: (data == null ? void 0 : data.notifications.sort((a, b) => {
      if (a.title > b.title) return 1;
      if (a.title < b.title) return -1;
      return 0;
    })) || []
  };
}
export function useListGitRepos({ page, perPage, query = void 0, orderBy = void 0, direction = void 0 }, enabled = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["list-git-repos", page, perPage, query, orderBy, direction],
    queryFn: () => defaultOnError(
      listGitRepos(page, perPage, query, orderBy, direction),
      i18n.t("notifications.fetch_git_repositories_failed", { ns: "enterprise_security_sigma" })
    ),
    retry: 1,
    enabled,
    placeholderData: keepPreviousData
  });
  return {
    loadingRepos: isLoading,
    repos: isLoading ? [] : data == null ? void 0 : data.sigma_git_repositories,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useListRepoContent(repoId, path, query) {
  const { data, isLoading } = useQuery({
    queryKey: ["list-git-repo-content", repoId, path, query],
    queryFn: () => defaultOnError(
      listRepoContent(repoId, path, query),
      i18n.t("notifications.list_repository_failed", { ns: "enterprise_security_sigma" })
    ),
    retry: 1,
    enabled: !!repoId
  });
  return {
    loadingRepoRules: isLoading && !!repoId,
    repoRules: data ? data.sort((a, b) => {
      if (a.type > b.type) return 1;
      if (a.type < b.type) return -1;
      return 0;
    }) : null
  };
}
export function useAddGitRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addGitRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const error = data.success_count > 0 ? {
          type: "warning",
          message: i18n.t("notifications.repo_added_partially", {
            ns: "enterprise_security_sigma",
            successCount: data.success_count,
            failureCount: data.failure_count
          })
        } : {
          type: "error",
          message: i18n.t("notifications.failed_add_repo", {
            ns: "enterprise_security_sigma",
            errors: data.errors
          })
        };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(
          i18n.t("notifications.repo_added_and_all_rules_imported", {
            ns: "enterprise_security_sigma",
            successCount: data.success_count
          })
        );
      }
      queryClient.invalidateQueries({
        queryKey: ["list-git-repos"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addGitRepo: mutateAsync, addingRepo: isLoading };
}
export function useAddDefaultGitRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addDefaultRepo,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["list-git-repos"]
    }),
    onError: (error) => UserNotification.error(error.message)
  });
  return { addDefaultRepo: mutateAsync, addingDefaultRepo: isLoading };
}
export function useUpdateGitRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateGitRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const error = data.success_count > 0 ? {
          type: "warning",
          message: i18n.t("notifications.repo_updated_partially", {
            ns: "enterprise_security_sigma",
            successCount: data.success_count,
            failureCount: data.failure_count
          })
        } : {
          type: "error",
          message: i18n.t("notifications.failed_update_repo", {
            ns: "enterprise_security_sigma",
            errors: data.errors
          })
        };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(
          i18n.t("notifications.repo_updated_with_all_rules", {
            ns: "enterprise_security_sigma",
            successCount: data.success_count
          })
        );
      }
      queryClient.invalidateQueries({
        queryKey: ["list-git-repos"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updateGitRepo: mutateAsync, updatingRepo: isLoading };
}
export function useImportAllRulesFromRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: importAllRulesFromRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const warnMsg = i18n.t("notifications.import_partially_succeeded", {
          ns: "enterprise_security_sigma",
          successRules: ruleCount(data.success_count),
          failureRules: ruleCount(data.failure_count)
        });
        const error = data.success_count > 0 ? { type: "warning", message: warnMsg } : {
          type: "error",
          message: i18n.t("notifications.all_rules_failed_to_import", { ns: "enterprise_security_sigma" })
        };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(
          i18n.t("notifications.all_rules_imported", {
            ns: "enterprise_security_sigma",
            successCount: data.success_count
          })
        );
      }
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    importRulesFromRepo: mutateAsync,
    importingRules: isLoading
  };
}
export function useRefreshRepoRules() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: refreshRepoRules,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const warnMsg = i18n.t("notifications.refresh_partially_succeeded", {
          ns: "enterprise_security_sigma",
          successRules: ruleCount(data.success_count),
          failureRules: ruleCount(data.failure_count)
        });
        const error = data.success_count > 0 ? { type: "warning", message: warnMsg } : {
          type: "error",
          message: i18n.t("notifications.all_rules_failed_to_refresh", { ns: "enterprise_security_sigma" })
        };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(
          i18n.t("notifications.all_rules_refreshed", {
            ns: "enterprise_security_sigma",
            successCount: data.success_count
          })
        );
      }
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    refreshRules: mutateAsync,
    refreshingRules: isLoading
  };
}
export function useDeleteSigmaRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteSigmaRepo,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["list-git-repos"]
    }),
    onError: (error) => UserNotification.error(error.message)
  });
  return { deleteSigmaRepo: mutateAsync, deletingRepo: isLoading };
}
export function useBulkDeleteSigmaRepos() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkSigmaRepoDelete,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.action_completed", { ns: "enterprise_security_sigma" }));
      queryClient.invalidateQueries({
        queryKey: ["list-git-repos"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkDeleteSigmaRepos: mutateAsync, isMutating: isLoading };
}
export function useValidateSigmaRuleZip() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: validateSigmaRuleZip,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    validateSigmaRuleZip: mutateAsync,
    isValidating: isLoading
  };
}
const ruleCount = (count) => i18n.t("notifications.rule_count", { ns: "enterprise_security_sigma", count });
const ruleDisplayName = (r) => {
  var _a, _b;
  return (_b = (_a = r.event_definition_name) != null ? _a : r.source_name) != null ? _b : i18n.t("notifications.unknown_rule", { ns: "enterprise_security_sigma" });
};
function notifyImportResult(data) {
  var _a;
  const results = (_a = data.results) != null ? _a : [];
  const hasResults = results.length > 0;
  const created = hasResults ? results.filter((r) => r.status === "created") : [];
  const skipped = hasResults ? results.filter((r) => r.status === "skipped") : [];
  const failed = hasResults ? results.filter((r) => r.status === "failed") : [];
  if (failed.length > 0) {
    const message = failed.map(
      (r) => {
        var _a2;
        return `${ruleDisplayName(r)}: ${(_a2 = r.error) != null ? _a2 : i18n.t("notifications.unknown_error", { ns: "enterprise_security_sigma" })}`;
      }
    ).join(", ");
    UserNotification.error(
      i18n.t("notifications.failed_import", {
        ns: "enterprise_security_sigma",
        failedRules: ruleCount(failed.length),
        message
      })
    );
  } else if (skipped.length > 0) {
    const importedCount = hasResults ? created.length : data.success_count;
    const names = skipped.map(ruleDisplayName).join(", ");
    UserNotification.warning(
      i18n.t("notifications.import_skipped_summary", {
        ns: "enterprise_security_sigma",
        importedRules: ruleCount(importedCount),
        skippedCount: skipped.length,
        names
      })
    );
  } else {
    const importedCount = hasResults ? created.length : data.success_count;
    UserNotification.success(
      i18n.t("notifications.import_success_summary", {
        ns: "enterprise_security_sigma",
        importedRules: ruleCount(importedCount)
      })
    );
  }
}
export function useValidateSigmaRuleYaml() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: validateSigmaRuleYaml,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    validateSigmaRuleYaml: mutateAsync,
    isValidating: isLoading
  };
}
export function useBulkImportFromRepo() {
  const { mutateAsync, isPending: isImporting } = useMutation({
    mutationFn: bulkImportFromRepo,
    onSuccess: notifyImportResult,
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkImportFromRepo: mutateAsync, isImporting };
}
export function useBulkUploadEventDefs() {
  const { mutateAsync, isPending: isUploading } = useMutation({
    mutationFn: bulkUploadEventDefs,
    onSuccess: notifyImportResult,
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkUploadEventDefs: mutateAsync, isUploading };
}
